import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  List,
  ListItem,
  Notice,
  Skeleton,
  StateScreen,
  Switch,
} from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { Screen } from '../app/chrome.js';
import { api } from '../lib/api.js';
import { noticeSentence, noticeTarget } from '../lib/notices.js';
import { formatIst } from '../lib/time.js';

interface NoticeRow {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}
interface Preferences {
  renewalReminders: boolean;
  visitRequests: boolean;
}

export function NotificationsScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const key = ['notifications', auth.agencyId, auth.current?.memberId];
  const notices = useQuery({ queryKey: key, queryFn: () => api<NoticeRow[]>('/notifications') });
  const preferences = useQuery({
    queryKey: ['notification-preferences', auth.agencyId, auth.current?.memberId],
    queryFn: () => api<Preferences>(`/notification-preferences/${auth.current?.memberId ?? ''}`),
    // Accounts only get money notices, which these two settings do not touch.
    enabled: auth.current !== null && auth.current.role !== 'accounts',
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST', body: {} }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const save = useMutation({
    mutationFn: (value: Preferences) =>
      api<Preferences>(`/notification-preferences/${auth.current?.memberId ?? ''}`, {
        method: 'PUT',
        body: value,
      }),
    onSuccess: (value) =>
      cache.setQueryData(
        ['notification-preferences', auth.agencyId, auth.current?.memberId],
        value,
      ),
  });
  const rows = notices.data ?? [];
  const unread = rows.filter((n) => n.readAt === null).length;
  return (
    <Screen title={t('notifications.title')} large eyebrow={auth.current?.agencyName}>
      {notices.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void notices.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('notifications.loadFailed')}
        </Notice>
      ) : null}
      {notices.isPending ? <Skeleton height={64} /> : null}
      {notices.isSuccess && rows.length === 0 ? (
        <StateScreen kind="empty" compact title={t('notifications.none')} />
      ) : null}
      {unread > 0 ? (
        <div className="card-head">
          <p className="k-t-label k-muted">{t('notifications.unread', { count: unread })}</p>
          <Button variant="text" loading={markAll.isPending} onClick={() => markAll.mutate()}>
            {t('notifications.markAll')}
          </Button>
        </div>
      ) : null}
      {rows.length === 0 ? null : (
        <List label={t('notifications.title')}>
          {rows.map((n) => {
            const sentence = noticeSentence(n.type, n.payload);
            return (
              <ListItem
                key={n.id}
                title={t(sentence.key, sentence.values)}
                supporting={formatIst(n.createdAt)}
                trailing={n.readAt === null ? <Badge label={t('notifications.new')} /> : undefined}
                onClick={() => {
                  if (n.readAt === null) markRead.mutate(n.id);
                  void navigate(noticeTarget(n.type, n.payload));
                }}
              />
            );
          })}
        </List>
      )}
      {preferences.data === undefined ? null : (
        <Card>
          <h2 className="k-t-title-sm">{t('notifications.preferences')}</h2>
          <Switch
            label={t('notifications.renewalPreference')}
            checked={preferences.data.renewalReminders}
            onChange={(e) =>
              save.mutate({ ...preferences.data, renewalReminders: e.target.checked })
            }
          />
          <Switch
            label={t('notifications.visitPreference')}
            checked={preferences.data.visitRequests}
            onChange={(e) => save.mutate({ ...preferences.data, visitRequests: e.target.checked })}
          />
        </Card>
      )}
    </Screen>
  );
}
